import React, { useCallback, useEffect, useMemo } from 'react'

import { Box, Grid, Tooltip } from '@mui/material'
import { formatDuration, intervalToDuration, isValid } from 'date-fns'
import { useFieldArray, useForm } from 'react-hook-form'

import {
  ControlledTextField,
  TextInput,
  ControlledSelector,
  ISelectorOptions,
  ControlledTimePicker,
  ControlledDateTimePicker,
} from 'components/common'
import { GetBookingQuery, GetSupplierBookingQuery } from 'generated/graphql'

import { IPoRow, PoTable } from '../PoTable'
import PackagesDetails from './PackagesDetails'

interface IProps {
  deliveryAddresses: NonNullable<GetBookingQuery['booking']>['addresses'] | undefined
  bookingPackages:
    | NonNullable<GetBookingQuery['booking']>['packages']
    | NonNullable<GetSupplierBookingQuery['supplierBooking']>['packages']
    | undefined
  selectedAddress: NonNullable<GetBookingQuery['booking']>['addresses'][0] | undefined
  onSelectedAddressIdChange: (string) => void
  control: any
  watch: any
  errors: any
  hoverText: string
  disabled: boolean
}

const WaitingTimeDetails = (props: IProps) => {
  const {
    deliveryAddresses,
    bookingPackages,
    selectedAddress,
    onSelectedAddressIdChange,
    control,
    watch,
    errors,
    hoverText,
    disabled,
  } = props

  const { fields } = useFieldArray({
    control,
    name: 'packages',
    keyName: 'packagesKey',
  })

  const {
    control: addressControl,
    watch: addressWatch,
    reset: addressReset,
  } = useForm({
    shouldUnregister: true,
    defaultValues: { selectedAddressId: '' },
  })

  const driverTimeFrom = watch('driverTimeFrom')
  const driverTimeTo = watch('driverTimeTo')

  const totalDriverTime = useMemo(() => {
    const driverTimeFromDate = new Date(driverTimeFrom)
    const driverTimeToDate = new Date(driverTimeTo)

    if (isValid(driverTimeFromDate) && isValid(driverTimeToDate)) {
      const { hours: durationHours, minutes: durationMinutes } = intervalToDuration({
        start: driverTimeFromDate,
        end: driverTimeToDate,
      })
      return formatDuration(
        { hours: durationHours, minutes: durationMinutes },
        { format: ['hours', 'minutes'], zero: true },
      )
    }
    return formatDuration(
      {
        hours: 0,
        minutes: 0,
      },
      { format: ['hours', 'minutes'], zero: true },
    )
  }, [driverTimeFrom, driverTimeTo])

  const addressesOptions: ISelectorOptions = useMemo(
    () =>
      deliveryAddresses?.map((ca, index) => ({
        value: ca.id,
        label: `Delivery Address ${index + 1}`,
      })) || [],
    [deliveryAddresses],
  )

  const addressDetails = useMemo(() => {
    const addressData = selectedAddress?.address
    const addressLine2 = addressData?.addressLine2 ? `${addressData?.addressLine2}, ` : ''

    return `${addressData?.name}, ${addressData?.addressLine1}, ${addressLine2}${addressData?.city}`
  }, [selectedAddress])

  const podRows = useMemo(
    () =>
      deliveryAddresses?.map((da) => {
        const addressData = da.address
        const addressLine2 = addressData?.addressLine2 ? `${addressData?.addressLine2}, ` : ''
        const address =
          `${addressData?.name}, ${addressData?.addressLine1},` +
          ` ${addressLine2}${addressData?.city}`
        const cxWeight = da.courierServiceWeight
          ? `${da.courierServiceWeight} (${da.courierServiceWeightUnit})`
          : ''

        return {
          id: da.id,
          address,
          dateTime: da.signedAt,
          wtTime: da.waitingTime,
          cxNumberOfItems: da.courierServiceNumberOfItems,
          cxWeight,
          signedBy: da.signedBy,
          sequenceOrder: da.sequenceOrder,
        } as IPoRow
      }) || [],
    [deliveryAddresses],
  )

  useEffect(() => {
    if (selectedAddress?.id) {
      addressReset({ selectedAddressId: selectedAddress.id })
    }
  }, [selectedAddress?.id, addressReset])

  useEffect(() => {
    const subscription = addressWatch((data, { name }) => {
      if (name === 'selectedAddressId') {
        onSelectedAddressIdChange(String(data.selectedAddressId))
      }
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [addressWatch, onSelectedAddressIdChange])

  const getPackageName = useCallback(
    (packageId: string) => {
      if (bookingPackages) {
        const packIndex = bookingPackages.findIndex(
          (bookingPackage) => String(bookingPackage.id) === String(packageId),
        )
        return `Pack ${packIndex !== -1 ? packIndex + 1 : 'unknown'}`
      } else {
        return 'Pack unknown'
      }
    },
    [bookingPackages],
  )

  return (
    <Tooltip title={disabled ? hoverText : ''} followCursor>
      <div>
        <Grid container columnSpacing={3} rowSpacing={1.5}>
          <Grid item xs={12} sm={6}>
            <ControlledSelector
              control={addressControl}
              label='Address'
              name='selectedAddressId'
              options={addressesOptions}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <ControlledDateTimePicker
              control={control}
              name='signedAt'
              label='Signed At'
              defaultValue={null}
              required
              disabled={disabled}
              error={!!errors.signedAt}
              helperText={errors.signedAt?.message}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <ControlledTextField
              control={control}
              name='waitingTime'
              label='Wt. Time'
              type='text'
              inputMode='numeric'
              pattern='[0-9]*'
              sanitizedNumber
              endAdornment='mins'
              defaultValue=''
              min='0'
              disabled={disabled}
              error={!!errors.waitingTime}
              helperText={errors.waitingTime?.message}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextInput
              name='addressDetails'
              label='Address Details'
              value={addressDetails}
              readOnly
              disabled={disabled}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <ControlledTextField
              control={control}
              name='signedByFirstName'
              label='Signed By First Name'
              defaultValue=''
              required
              disabled={disabled}
              error={!!errors.signedByFirstName}
              helperText={errors.signedByFirstName?.message}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <ControlledTextField
              control={control}
              name='signedByLastName'
              label='Signed By Last Name'
              defaultValue=''
              required
              disabled={disabled}
              error={!!errors.signedByLastName}
              helperText={errors.signedByLastName?.message}
            />
          </Grid>

          {fields.length > 0 &&
            fields.map((field, index) => (
              <PackagesDetails
                key={field.packagesKey}
                field={field}
                disabled={disabled}
                control={control}
                index={index}
                errors={errors}
                getPackageName={getPackageName}
              />
            ))}

          <Grid item xs={12} sm={6}>
            <ControlledDateTimePicker
              control={control}
              name='arrivedAt'
              label='Arrived At'
              defaultValue={null}
              required
              disabled={disabled}
              error={!!errors.arrivedAt}
              helperText={errors.arrivedAt?.message}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <ControlledTextField
              control={control}
              name='noteFromDriver'
              label='Notes'
              multiline
              disabled={disabled}
              error={!!errors.noteFromDriver}
              helperText={errors.noteFromDriver?.message}
            />
          </Grid>

          <Grid item xs={12}>
            <Grid container columnSpacing={3}>
              <Grid item xs={12} sm={4}>
                <ControlledTimePicker
                  control={control}
                  name='driverTimeFrom'
                  label='Driver Time From'
                  disabled={disabled}
                  error={!!errors.driverTimeFrom}
                  helperText={errors.driverTimeFrom?.message}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <ControlledTimePicker
                  control={control}
                  name='driverTimeTo'
                  label='Driver Time To'
                  disabled={disabled}
                  error={!!errors.driverTimeTo}
                  helperText={errors.driverTimeTo?.message}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextInput
                  name='totalDriverTime'
                  label='Total'
                  value={totalDriverTime}
                  readOnly
                  disabled={disabled}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Box my={2}>
              <PoTable rows={podRows} selectedAddressId={selectedAddress?.id} />
            </Box>
          </Grid>
        </Grid>
      </div>
    </Tooltip>
  )
}

export default WaitingTimeDetails
